package com.example.mystybag.core.logic.base

import com.arkivanov.decompose.router.slot.SlotNavigation
import com.arkivanov.decompose.router.slot.activate
import com.arkivanov.decompose.router.slot.dismiss
import com.arkivanov.decompose.router.stack.bringToFront
import com.arkivanov.decompose.router.stack.pop
import com.arkivanov.decompose.router.stack.pushNew
import com.arkivanov.decompose.router.stack.replaceCurrent
import com.example.mystybag.core.defines.UtilsActionsDefines.ACTION_NONE
import com.example.mystybag.core.expected.NetworkConnectionHelper.internetConnectionAvailable
import com.example.mystybag.core.expected.ScreenCastingListener.setupScreenCastingListener
import com.example.mystybag.core.extensions.deleteFirst
import com.example.mystybag.core.extensions.safeCast
import com.example.mystybag.core.logic.helpers.AppSpecificViewModelInterface
import com.example.mystybag.core.logic.helpers.DialogResult
import com.example.mystybag.core.logic.helpers.DialogResultType
import com.example.mystybag.core.logic.helpers.UIDialogImpl
import com.example.mystybag.core.logic.helpers.UIDialogInterface
import com.example.mystybag.core.model.FullScreenImageOverlay
import com.example.mystybag.core.model.PendingSnackbar
import com.example.mystybag.core.model.SnackbarConfig
import com.example.mystybag.core.security.Security
import com.example.mystybag.core.views.AppThemeColors
import com.example.mystybag.core.views.currentTheme
import com.example.mystybag.core.views.isDarkTheme
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.yield
import kotlin.reflect.KClass

@OptIn(kotlin.experimental.ExperimentalNativeApi::class)
abstract class AppViewModel(context: AppContext, config: NavigatorConfig): ParentViewModel(context, config, ACTION_NONE),
    FrameworkInterface
{

    companion object: FrameworkInterface {

        private var framework: FrameworkInterface? = null

        fun setup(frameworkInterface: FrameworkInterface) {
            framework = frameworkInterface
        }

        val iosAppBlurScreen         = MutableStateFlow(false)
        val fullScreenImageOverlay   = MutableStateFlow<FullScreenImageOverlay?>(null)
        var blurScreenOnAppPause: Boolean = false
        var isDialogOpen        : Boolean = false
            private set

        override fun getTopViewModel()                                                  = framework?.getTopViewModel()
        override fun isMainInStack()                                                    = framework?.isMainInStack() ?: false
        override fun dumpNavigationStack()                                              { framework?.dumpNavigationStack() }
        override fun showSnackbar(config: SnackbarConfig)                               { framework?.showSnackbar(config) }
        override fun navigateBack()                                                     { framework?.navigateBack() }
        override fun navigateTo(config: FeatureManager, callback: ParentCallback?)      { framework?.navigateTo(config, callback) }
        override fun bringToFront(config: NavigatorConfig, callback: ParentCallback?)   { framework?.bringToFront(config) }
        override fun replaceCurrent(config: FeatureManager, callback: ParentCallback?)  { framework?.replaceCurrent(config, callback) }
        override suspend fun displayDialog(config: DialogConfig)                        = framework?.displayDialog(config) ?: DialogResult(DialogResultType.CANCELED)
        override fun baseBottomSheetDidClose()                                          = framework?.baseBottomSheetDidClose() ?: false
        override fun setDarkTheme(isDark: Boolean)                                      = framework?.setDarkTheme(isDark) ?: Unit
        override fun <T: NavigatorConfig> navigatorIsOpen(clazz: KClass<T>)             = framework?.navigatorIsOpen(clazz) ?: false

        fun setIsIOSAppOnBackground(onBackground: Boolean) {
            iosAppBlurScreen.value = onBackground && blurScreenOnAppPause
        }
    }

    private val dialogControl           : UIDialogInterface                 = UIDialogImpl(context)
    private val mutex                   : Mutex                             = Mutex()
    private val pendingSnackbarQueue    : MutableList<PendingSnackbar>      = mutableListOf()
    private val currentSnackbar         : SlotNavigation<SnackbarConfig>    = SlotNavigation()

    val snackState = context.snackChildSlot(
        source           = currentSnackbar,
        key              = "SnackSlot@AppViewModel",
        handleBackButton = false
    )

    init {
        /*Security.validateJWT()
        setupScreenCastingListener()

        dialogControl.dialogState.subscribe {
            isDialogOpen = it.child != null
        }*/
    }

    override fun setDarkTheme(isDark: Boolean) {
        isDarkTheme.value = isDark
        currentTheme.value = AppThemeColors()
    }

    override suspend fun displayDialog(config: DialogConfig): DialogResult = dialogControl.showDialog(config)

    override fun getTopViewModel() = viewModelOnTop ?: this

    override fun getCallbacks(config: NavigatorConfig): ParentCallback {
        return pendingNavigation.remove(config) ?: this
    }

    override fun dumpNavigationStack() = dumpStack()

    override fun showSnackbar(config: SnackbarConfig) {
        launchOnMain {
            mutex.withLock {
                PendingSnackbar(config).also { snack ->
                    if (!pendingSnackbarQueue.contains(snack))
                        pendingSnackbarQueue.add(snack)

                    if (snackState.value.child?.instance == null)
                        currentSnackbar.activate(snack.config)
                }
            }
        }
    }

    override fun navigateTo(config: FeatureManager, callback: ParentCallback?) {
        callback?.also {
            pendingNavigation[config] = it
        }
        navigation.pushNew(config)
    }

    override fun bringToFront(config: NavigatorConfig, callback: ParentCallback?) {
        callback?.also {
            pendingNavigation[config] = it
        }
        navigation.bringToFront(config)
    }

    override fun replaceCurrent(config: FeatureManager, callback: ParentCallback?) {
        callback?.also {
            pendingNavigation[config] = it
        }
        navigation.replaceCurrent(config)
    }

    override fun navigateBack() {
        navigation.pop()
    }

    @Suppress("UNUSED_PARAMETER")
    fun onSnackbarClosed(actionPerformed: Boolean) {
        currentSnackbar.dismiss()
        launchOnMain {
            mutex.withLock {
                if (pendingSnackbarQueue.isNotEmpty())
                    pendingSnackbarQueue.deleteFirst()
                pendingSnackbarQueue.firstOrNull()?.config
            }?.also {
                yield()
                currentSnackbar.activate(it)

            }
        }
    }

    override fun baseBottomSheetDidClose(): Boolean {
        return if (currentlyOpenBottomSheet != null) {
            closeBottomSheet()
            true
        } else
            false
    }

    fun getViewInterface(): AppSpecificViewModelInterface {
        return object: AppSpecificViewModelInterface {
            override val snackState         = this@AppViewModel.snackState
            override val childStack         = this@AppViewModel.childStack
            override val sheetControl       = this@AppViewModel
            override val dialogControl      = this@AppViewModel.dialogControl
            override val uiState            = this@AppViewModel.baseUIState
            override fun onSnackbarClosed(actionPerformed: Boolean) = this@AppViewModel.onSnackbarClosed(actionPerformed)
        }
    }
}